/*global google*/

const OverlayType = {
    POLYGON: 'polygon',
    POLYLINE: 'polyline'
};

class CustomDrawingManager {
    constructor(options = {}) {
        this._options = options;
        this._map = null;
        this._drawingMode = null;
        this._eventListeners = {};
        this._mapListeners = [];
        this._tempPath = [];
        this._tempShape = null;
        this._originalCursor = null;
        this._originalDisableDoubleClick = null;
    }

    setMap(map) {
        if (map === this._map) return;
        if (this._map) {
            this._cleanup();
        }
        this._map = map;
        if (map && this._drawingMode) {
            this._startListening();
        }
    }

    setDrawingMode(mode) {
        this._cleanup();
        this._drawingMode = mode;
        if (mode && this._map) {
            this._startListening();
        }
    }

    addListener(event, callback) {
        if (!this._eventListeners[event]) {
            this._eventListeners[event] = [];
        }
        this._eventListeners[event].push(callback);
        return {
            remove: () => {
                this._eventListeners[event] = this._eventListeners[event].filter(cb => cb !== callback);
            }
        };
    }

    _emit(event, data) {
        (this._eventListeners[event] || []).forEach(cb => cb(data));
    }

    _startListening() {
        this._tempPath = [];

        this._originalDisableDoubleClick = this._map.get('disableDoubleClickZoom');
        this._map.set('disableDoubleClickZoom', true);

        this._originalCursor = this._map.get('draggableCursor');
        this._map.setOptions({ draggableCursor: 'crosshair' });

        const clickListener = this._map.addListener('click', (evt) => {
            this._tempPath.push(evt.latLng);
            this._updateTempShape(this._tempPath);
        });

        const dblClickListener = this._map.addListener('dblclick', () => {
            // A click fires just before dblclick, remove that point then complete
            if (this._tempPath.length > 0) {
                this._tempPath.pop();
            }
            this._completeDraw();
        });

        const mouseMoveListener = this._map.addListener('mousemove', (evt) => {
            if (this._tempPath.length > 0) {
                this._updateTempShape([...this._tempPath, evt.latLng]);
            }
        });

        this._mapListeners = [clickListener, dblClickListener, mouseMoveListener];
    }

    _updateTempShape(path) {
        if (this._drawingMode === OverlayType.POLYGON) {
            if (!this._tempShape) {
                this._tempShape = new google.maps.Polygon({
                    ...(this._options.polygonOptions || {}),
                    paths: path,
                    map: this._map,
                    clickable: false
                });
            } else {
                this._tempShape.setPath(path);
            }
        } else if (this._drawingMode === OverlayType.POLYLINE) {
            if (!this._tempShape) {
                this._tempShape = new google.maps.Polyline({
                    ...(this._options.polylineOptions || {}),
                    path: path,
                    map: this._map,
                    clickable: false
                });
            } else {
                this._tempShape.setPath(path);
            }
        }
    }

    _completeDraw() {
        const path = [...this._tempPath];
        const drawingMode = this._drawingMode;

        this._cleanup();

        if (drawingMode === OverlayType.POLYGON && path.length >= 3) {
            const polygon = new google.maps.Polygon({
                ...(this._options.polygonOptions || {}),
                paths: path
            });
            this._emit('polygoncomplete', polygon);
        } else if (drawingMode === OverlayType.POLYLINE && path.length >= 2) {
            const polyline = new google.maps.Polyline({
                ...(this._options.polylineOptions || {}),
                path: path
            });
            this._emit('polylinecomplete', polyline);
        }
    }

    _cleanup() {
        this._mapListeners.forEach(l => {
            if (l && typeof l.remove === 'function') l.remove();
            else if (l) google.maps.event.removeListener(l);
        });
        this._mapListeners = [];

        if (this._tempShape) {
            this._tempShape.setMap(null);
            this._tempShape = null;
        }

        this._tempPath = [];

        if (this._map) {
            this._map.set('disableDoubleClickZoom', this._originalDisableDoubleClick);
            this._map.setOptions({ draggableCursor: this._originalCursor || null });
        }
    }
}

export { CustomDrawingManager, OverlayType };
